import React, { useCallback, useEffect, useMemo } from 'react';

import { CONSTANTS } from '@/data';
import { useUserStore } from '@/hooks/use-user-store';

import { AblyService } from './ably';

type WebsocketContextType = {
    websocketService: AblyService | undefined;
    connected: boolean;
    authenticated: boolean;
};

const WebsocketContext = React.createContext<WebsocketContextType>({
    websocketService: undefined,
    connected: false,
    authenticated: false,
});

export const WebsocketProvider = ({ children }: { children: React.ReactNode }) => {
    const [connected, setConnected] = React.useState(false);
    const [authenticated, setAuthenticated] = React.useState(false);
    const session = useUserStore(state => state.session);

    const onConnected = useCallback(() => {
        setConnected(true);
    }, [setConnected]);

    const onDisconnected = useCallback(() => {
        setConnected(false);
    }, [setConnected]);

    const onAuthenticated = useCallback(() => {
        setAuthenticated(true);
    }, [setAuthenticated]);

    // Create new websocket service when session changes
    const websocketService = useMemo(() => {
        if (!session?.access_token) {
            return undefined;
        }
        return new AblyService(CONSTANTS.ABLY_API_KEY);
    }, [session?.access_token]);

    useEffect(() => {
        if (!websocketService) {
            return;
        }
        websocketService.addListener('connected', onConnected);
        websocketService.addListener('disconnected', onDisconnected);
        websocketService.addListener('closed', onDisconnected);
        websocketService.initializeListeners();
        return () => {
            setAuthenticated(false);
            websocketService?.removeListener('connected', onConnected);
            websocketService?.removeListener('disconnected', onDisconnected);
            websocketService?.removeListener('closed', onDisconnected);
        };
    }, [onConnected, onDisconnected, websocketService]);

    useEffect(() => {
        if (session?.access_token && !authenticated) {
            websocketService?.authenticate(session?.access_token, onAuthenticated);
        }
        if (!session?.access_token && connected) {
            setAuthenticated(false);
        }
    }, [
        connected,
        onConnected,
        onDisconnected,
        session?.access_token,
        websocketService,
        onAuthenticated,
        authenticated,
    ]);

    const value = useMemo(
        () => ({
            websocketService,
            connected,
            authenticated,
        }),
        [websocketService, connected, authenticated]
    );

    return <WebsocketContext.Provider value={value}>{children}</WebsocketContext.Provider>;
};

export const useWebsocket = () => {
    const context = React.useContext(WebsocketContext);
    if (context === undefined) {
        throw new Error('useWebsocket must be used within a WebsocketProvider');
    }
    return context;
};
